import { useEffect, useState } from "react";
import { Loader2, Save, Send, Mail, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Switch } from "@/components/ui/switch";

const EMPTY = {
  enabled: false, host: "", port: 465, username: "", password: "",
  from_name: "SMT Provence", from_email: "", recipients: "", notify_on_lead: true,
};

export default function SettingsAdmin() {
  const [form, setForm] = useState(EMPTY);
  const [hasPassword, setHasPassword] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testTo, setTestTo] = useState("");

  useEffect(() => {
    api.get("/admin/settings/smtp")
      .then((r) => {
        const d = r.data || {};
        setForm({ ...EMPTY, ...d, password: "" });
        setHasPassword(!!d.has_password);
      })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, []);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, port: Number(form.port) || 465 };
      if (!payload.password) delete payload.password;
      await api.put("/admin/settings/smtp", payload);
      if (payload.password) setHasPassword(true);
      setForm((f) => ({ ...f, password: "" }));
      toast.success("Paramètres enregistrés");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const r = await api.post("/admin/settings/smtp/test", { to: testTo });
      toast.success(`Email de test envoyé à ${r.data.sent_to}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setTesting(false); }
  };

  const field = "w-full rounded-lg border border-border px-3 py-2 text-sm text-ink outline-none focus:ring-2 focus:ring-primary";
  const lab = "text-xs font-semibold text-ink/60 block mb-1";

  if (loading) return <div className="flex items-center justify-center py-24"><Loader2 className="animate-spin text-primary" /></div>;

  return (
    <div data-testid="settings-page">
      <h1 className="font-heading text-3xl font-black text-ink">Paramètres</h1>
      <p className="text-ink/50 mt-1">Configurez les notifications email envoyées lors des demandes de rappel.</p>

      <div className="mt-6 bg-card border border-border rounded-xl p-6 max-w-3xl">
        <div className="flex items-center gap-2 mb-5">
          <Mail size={18} className="text-primary" />
          <h2 className="font-heading font-bold text-ink text-lg">Serveur SMTP</h2>
        </div>

        <div className="flex items-center justify-between px-4 py-3 rounded-lg bg-muted/60 mb-5">
          <div>
            <p className="text-sm font-semibold text-ink">Activer l'envoi d'emails</p>
            <p className="text-xs text-ink/50">Les notifications ne partiront que si cette option est activée.</p>
          </div>
          <Switch checked={form.enabled} onCheckedChange={(v) => upd("enabled", v)} data-testid="smtp-enabled" />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="sm:col-span-2"><label className={lab}>Serveur (host)</label><input data-testid="smtp-host" className={field} value={form.host} onChange={(e) => upd("host", e.target.value)} placeholder="smtp.ionos.fr" /></div>
          <div><label className={lab}>Port</label><input data-testid="smtp-port" type="number" className={field} value={form.port} onChange={(e) => upd("port", e.target.value)} placeholder="465" /></div>
          <div><label className={lab}>Identifiant</label><input data-testid="smtp-username" className={field} value={form.username} onChange={(e) => upd("username", e.target.value)} placeholder="contact@smtprovence.fr" /></div>
          <div>
            <label className={lab}>Mot de passe {hasPassword && <span className="text-green-600">(enregistré)</span>}</label>
            <input data-testid="smtp-password" type="password" className={field} value={form.password} onChange={(e) => upd("password", e.target.value)} placeholder={hasPassword ? "•••••••• (inchangé)" : "Mot de passe SMTP"} />
          </div>
          <div><label className={lab}>Port 465 = SSL · 587 = TLS</label><div className="text-xs text-ink/40 pt-2 flex items-center gap-1"><ShieldCheck size={14} className="text-green-600" /> Connexion chiffrée automatique</div></div>
        </div>

        <div className="border-t border-border my-6" />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div><label className={lab}>Nom de l'expéditeur</label><input data-testid="smtp-from-name" className={field} value={form.from_name} onChange={(e) => upd("from_name", e.target.value)} placeholder="SMT Provence" /></div>
          <div><label className={lab}>Email de l'expéditeur</label><input data-testid="smtp-from-email" className={field} value={form.from_email} onChange={(e) => upd("from_email", e.target.value)} placeholder="contact@smtprovence.fr" /></div>
          <div className="sm:col-span-2"><label className={lab}>Destinataires des notifications</label><input data-testid="smtp-recipients" className={field} value={form.recipients} onChange={(e) => upd("recipients", e.target.value)} placeholder="contact@smtprovence.fr, michel@smtprovence.fr" /><p className="text-xs text-ink/40 mt-1">Séparez plusieurs adresses par une virgule.</p></div>
        </div>

        <div className="flex items-center justify-between px-4 py-3 rounded-lg bg-muted/60 mt-5">
          <div>
            <p className="text-sm font-semibold text-ink">Notifier à chaque demande de rappel</p>
            <p className="text-xs text-ink/50">Un email est envoyé automatiquement dès qu'un lead est créé.</p>
          </div>
          <Switch checked={form.notify_on_lead} onCheckedChange={(v) => upd("notify_on_lead", v)} data-testid="smtp-notify" />
        </div>

        <div className="flex flex-wrap items-center gap-3 mt-6">
          <button onClick={save} disabled={saving} data-testid="smtp-save" className="inline-flex items-center gap-2 bg-primary text-white font-bold px-6 py-2.5 rounded-full disabled:opacity-60">
            {saving ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />} Enregistrer
          </button>
        </div>
      </div>

      <div className="mt-6 bg-card border border-border rounded-xl p-6 max-w-3xl">
        <div className="flex items-center gap-2 mb-2"><Send size={18} className="text-primary" /><h2 className="font-heading font-bold text-ink text-lg">Tester la configuration</h2></div>
        <p className="text-xs text-ink/50 mb-4">Enregistrez d'abord vos réglages, puis envoyez un email de test.</p>
        <div className="flex flex-col sm:flex-row gap-3">
          <input data-testid="smtp-test-to" className={field + " sm:flex-1"} value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="Adresse de test (par défaut : 1er destinataire)" />
          <button onClick={sendTest} disabled={testing} data-testid="smtp-test-send" className="inline-flex items-center justify-center gap-2 bg-ink text-white font-bold px-6 py-2.5 rounded-full disabled:opacity-60 whitespace-nowrap">
            {testing ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />} Envoyer un test
          </button>
        </div>
      </div>
    </div>
  );
}
